const intentionalRemoves = new Set();

class CartDrawerSection extends HTMLElement {
  cartUpdateUnsubscriber = undefined;
  constructor() {
    super();

    this.cartType = this.dataset.cartType;
    this.drawerClass = "wt-cart__drawer";
    this.drawer = this.querySelector(`.${this.drawerClass}`);
    this.classDrawerActive = `${this.drawerClass}--open`;
    this.pageOverlayClass = "page-overlay-cart";
    this.activeOverlayBodyClass = `${this.pageOverlayClass}-on`;
    this.body = document.body;
    this.triggerQuery = [
      `.wt-cart__trigger`,
      `.wt-cart__back-link`,
      `.${this.pageOverlayClass}`,
    ].join(", ");
    this.triggers = () => document.querySelectorAll(this.triggerQuery);
    this.isOpen = false;
    this.isCartPage = window.location.pathname === window.routes.cart_url;
    this.closeButton = () => this.querySelector(".wt-cart__drawer__close");
    this.mainTrigger = document.querySelector(".wt-cart__trigger");
    this.toggleEelements = () =>
      this.querySelectorAll(this.dataset.toggleTabindex);
  }

  connectedCallback() {
    if (this.cartType === "page" || this.isCartPage) {
      document.addEventListener('cart-drawer:refresh', (e) => this.refreshCartDrawer(e))
      return;
    }

    this.init();
    this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, () => {
      if (this.isOpen) {
        setTabindex(this.toggleEelements(), "0");
        this.closeButton().focus();
      }
    });

    this.openFromRedirectParam();
  }

  openFromRedirectParam() {
    const params = new URLSearchParams(window.location.search);
    if (!params.has("open_cart")) return;

    params.delete("open_cart");
    const query = params.toString();
    const cleanUrl = window.location.pathname + (query ? `?${query}` : "") + window.location.hash;
    window.history.replaceState({}, "", cleanUrl);

    if (!this.isOpen) {
      this.toggleDrawerClasses();
    }
  }

  disconnectedCallback() {
    if (this.cartUpdateUnsubscriber) {
      this.cartUpdateUnsubscriber();
    }
  }

  getFocusableElements() {
    const focusableElementsSelector =
      "button, [href], input, select, [tabindex]";
    const focusableElements = () =>
      Array.from(this.querySelectorAll(focusableElementsSelector)).filter(
        (el) => !el.hasAttribute("disabled") && el.tabIndex >= 0,
      );

    return {
      focusableElements,
      first: focusableElements()[0],
      last: focusableElements()[focusableElements().length - 1],
    };
  }

  temporaryHideFocusVisible() {
    document.body.classList.add("no-focus-visible");
  }

  onToggle() {
    if (this.hasAttribute("open")) {
      this.removeAttribute("open");
      this.isOpen = false;
      this.mainTrigger.focus();
      this.temporaryHideFocusVisible();
      setTabindex(this.toggleEelements(), "-1");
    } else {
      this.setAttribute("open", "");
      this.isOpen = true;
      this.closeButton().focus();
      this.temporaryHideFocusVisible();
      setTabindex(this.toggleEelements(), "0");
    }
  }

  toggleDrawerClasses() {
    this.onToggle();
    this.drawer.classList.toggle(this.classDrawerActive);
    this.body.classList.toggle(this.activeOverlayBodyClass);
  }

  init() {
    this.addEventListener("keydown", (e) => {
      const isTabPressed =
        e.key === "Tab" || e.keyCode === 9 || e.code === "Tab";
      const { first, last } = this.getFocusableElements();

      if (e.key === "Escape" || e.keyCode === 27 || e.code === "Escape") {
        if (this.isOpen) {
          this.toggleDrawerClasses();
        }
      }

      if (isTabPressed) {
        if (e.shiftKey && document.activeElement === first) {
          last.focus();
          e.preventDefault();
        } else if (!e.shiftKey && document.activeElement === last) {
          first.focus();
          e.preventDefault();
        }
      }
    });

    this.triggers().forEach((trigger) => {
      trigger.addEventListener("click", (e) => {
        e.preventDefault();
        this.toggleDrawerClasses();
      });
    });

    this.addEventListener("click", (e) => {
      if (e.target.classList.contains("wt-cart__drawer__close")) {
        e.preventDefault();
        this.toggleDrawerClasses();
      }
    });

    document.addEventListener('cart-drawer:refresh', (e) => this.refreshCartDrawer(e))
  }

  captureCartItemsFromDom() {
    const items = new Map();
    document.querySelectorAll('#CartDrawer .js-counter-quantity[data-quantity-variant-id]').forEach((input) => {
      const variantId = input.dataset.quantityVariantId;
      const title = input.closest('.wt-cart__item')?.querySelector('.wt-cart__item__name a')?.textContent.trim() || '';
      if (variantId) items.set(variantId, title);
    });
    return items;
  }

  renderContents(parsedState, isClosedCart = true) {
    const beforeItems = this.captureCartItemsFromDom();

    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);
      sectionElement.innerHTML = this.getSectionInnerHTML(
        parsedState.sections[section.id],
        section.selector,
      );
    });

    if (isClosedCart) {
      setTimeout(() => {
        this.toggleDrawerClasses();
        if (this.isOpen) {
          this.closeButton().focus();
        }
      });
    }

    const afterItems = this.captureCartItemsFromDom();

    document.dispatchEvent(new CustomEvent("cart-notices:refresh"));
    document.dispatchEvent(new CustomEvent("cart-drawer:refreshed"));

    beforeItems.forEach((title, variantId) => {
      if (!afterItems.has(variantId) && !intentionalRemoves.has(variantId)) {
        const template = window.cartStrings?.autoRemovedNotice || '[title] se ha eliminado de tu cesta porque está agotado.';
        const message = template.replace('[title]', title);
        document.dispatchEvent(new CustomEvent('cart-notices:capture', {
          detail: { line: `removed-${variantId}`, message, showClose: true }
        }));
      }
    });
    intentionalRemoves.clear();
  }

  getSectionInnerHTML(html, selector = ".shopify-section") {
    return new DOMParser()
      .parseFromString(html, "text/html")
      .querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: "cart-drawer",
        selector: "#CartDrawer",
      },
      {
        id: "cart-icon-bubble",
      },
    ];
  }

  refreshCartDrawer() {
    const sectionsToRender = this.getSectionsToRender();
    fetch(`${window.Shopify.routes.root}?sections=${sectionsToRender[0].id},${sectionsToRender[1].id}`)
      .then(response => response.json())
      .then((response) => {
        const parsedState = {
          "sections": response
        }
        this.renderContents(parsedState, false);
      })
      .catch(e => {
        console.log(e)
      })
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define("cart-drawer", CartDrawerSection);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: "CartDrawer",
        section: "cart-drawer",
        selector: ".drawer__inner",
      },
      {
        id: "cart-icon-bubble",
        section: "cart-icon-bubble",
        selector: ".shopify-section",
      },
    ];
  }

  onCartUpdate() {
    // Dispatch cart-drawer:refresh to update the entire drawer
    // (header with item count, items list, footer with total price, and cart-icon-bubble)
    document.dispatchEvent(new CustomEvent("cart-drawer:refresh"));
  }
}

customElements.define("cart-drawer-items", CartDrawerItems);

// Bind variant_id and focus email for coming-soon modal inside cart drawer
(function initCartComingSoonBinding() {
  if (window.__comingSoonBindingInitialized) return;
  window.__comingSoonBindingInitialized = true;

  document.addEventListener('click', (ev) => {
    const btn = ev.target.closest('.brn-coming-soon__link[data-variant-id]');
    if (!btn) return;

    // Close the open size sheet before opening the modal
    const sheetRoot = btn.closest('[data-cart-size-modal].is-open');
    if (sheetRoot) {
      // Go through the sheet's own closer: it also moves the sheet back out of
      // <body>, where it is parked while open (see component-sheet-size-cart).
      if (typeof window.closeCartSizeSheet === 'function') {
        window.closeCartSizeSheet(sheetRoot);
      } else {
        sheetRoot.classList.remove('is-open');
        document.body.classList.remove('modal-open');
      }
    }

    const opener = btn.closest('modal-opener');
    const selector = opener && opener.getAttribute('data-modal');
    const modal = selector ? document.querySelector(selector) : null;
    if (!modal) return;

    const form = modal.querySelector('form.brn-coming-soon__form, .brn-coming-soon__form');
    if (!form) return;

    const hiddenVariant = form.querySelector('input[name="variant_id"]');
    if (hiddenVariant) hiddenVariant.value = btn.dataset.variantId || '';

    const email = form.querySelector('input[type="email"]');
    if (email) setTimeout(() => email.focus(), 100);
  }, true);


  // Clear error while typing
  document.addEventListener('input', (ev) => {
    const emailInput = ev.target.closest('input[type="email"]');
    if (!emailInput) return;
    const container = emailInput.closest('.brn-form') || emailInput.closest('form') || emailInput.closest('.brn-modal__content');
    const errorEl = container?.querySelector('[data-error-email]');
    emailInput.removeAttribute('aria-invalid');
    emailInput.classList.remove('has--error');
    if (errorEl) errorEl.hidden = true;
  }, true);

  // Validate on modal button click (works for submit/button types)
  document.addEventListener('click', (ev) => {
    const trigger = ev.target.closest('.brn-modal [type="submit"], .brn-modal [type="button"]');
    if (!trigger) return;

    const container = trigger.closest('.brn-form') || trigger.closest('form') || trigger.closest('.brn-modal__content');
    const emailInput = container?.querySelector('input[type="email"]');
    if (!emailInput) return;

    const value = (emailInput.value || '').trim();
    const errorEl = container.querySelector('[data-error-email]');
    const isValid = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(value);

    if (!isValid) {
      ev.preventDefault();
      emailInput.setAttribute('aria-invalid', 'true');
      emailInput.classList.add('has--error');
      if (errorEl) errorEl.hidden = false;
      setTimeout(() => emailInput.focus(), 50);
      return;
    }

    // Valid: optional network submit then emit coming-soon:submit
    const modal = trigger.closest('modal-dialog');
    const productId = container.querySelector('input[name="product_id"]')?.value || '';
    const variantId = container.querySelector('input[name="variant_id"]')?.value || '';

    const endpoint = window.config?.hydrogenEndpoint + '/rest/1/products/stock_reminder';
    if (endpoint && typeof endpoint === 'string') {
      try {
        fetch(endpoint, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${window.config?.hydrogenBearer}` },
          body: JSON.stringify({ product_id: productId, variant_id: variantId, email: value })
        }).then(async (res) => {
          const data = await res.json().catch(() => null);
          document.dispatchEvent(new CustomEvent('coming-soon:submit', { detail: { productId, variantId, email: value, modalId: modal?.id || null, ok: res.ok, status: res.status, data } }));
        }).catch(() => {
          document.dispatchEvent(new CustomEvent('coming-soon:submit', { detail: { productId, variantId, email: value, modalId: modal?.id || null, ok: false } }));
        });
      } catch {
        document.dispatchEvent(new CustomEvent('coming-soon:submit', { detail: { productId, variantId, email: value, modalId: modal?.id || null, ok: false } }));
      }
    } else {
      try {
        document.dispatchEvent(new CustomEvent('coming-soon:submit', { detail: { productId, variantId, email: value, modalId: modal?.id || null } }));
      } catch { }
    }
    ev.preventDefault();
  }, true);

  // Submit-like behavior on Enter key inside email input
  document.addEventListener('keydown', (ev) => {
    if (ev.key !== 'Enter') return;
    const emailInput = ev.target.closest('input[type="email"]');
    if (!emailInput) return;
    const container = emailInput.closest('.brn-form') || emailInput.closest('form') || emailInput.closest('.brn-modal__content');

    if (!container) return;

    // Reuse the same flow as click
    const value = (emailInput.value || '').trim();
    const errorEl = container.querySelector('[data-error-email]');
    const isValid = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(value);
    if (!isValid) {
      ev.preventDefault();
      emailInput.setAttribute('aria-invalid', 'true');
      emailInput.classList.add('has--error');
      if (errorEl) errorEl.hidden = false;
      setTimeout(() => emailInput.focus(), 50);
      return;
    }

    const modal = emailInput.closest('modal-dialog');
    const productId = container.querySelector('input[name="product_id"]')?.value || '';
    const variantId = container.querySelector('input[name="variant_id"]')?.value || '';
    const endpoint = window.config?.hydrogenEndpoint + '/rest/1/products/stock_reminder';
    if (endpoint && typeof endpoint === 'string') {
      try {
        fetch(endpoint, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${window.config?.hydrogenBearer || ''}` },
          body: JSON.stringify({ product_id: productId, variant_id: variantId, email: value, shop_id: window.Shopify?.shopId || '' })
        }).then(async (res) => {
          const data = await res.json().catch(() => null);
          document.dispatchEvent(new CustomEvent('coming-soon:submit', { detail: { productId, variantId, email: value, modalId: modal?.id || null, ok: res.ok, status: res.status, data } }));
        }).catch(() => {
          document.dispatchEvent(new CustomEvent('coming-soon:submit', { detail: { productId, variantId, email: value, modalId: modal?.id || null, ok: false } }));
        });
      } catch {
        document.dispatchEvent(new CustomEvent('coming-soon:submit', { detail: { productId, variantId, email: value, modalId: modal?.id || null, ok: false } }));
      }
    } else {
      try {
        document.dispatchEvent(new CustomEvent('coming-soon:submit', { detail: { productId, variantId, email: value, modalId: modal?.id || null } }));
      } catch { }
    }
    ev.preventDefault();
  }, true);

  // Show success UI on coming-soon submit
  document.addEventListener('coming-soon:submit', (ev) => {
    const detail = ev.detail || {};
    // Treat as success when ok !== false (no endpoint or successful response)
    if (detail && detail.ok === false) return;
    const modal = detail.modalId ? document.getElementById(detail.modalId) : document.querySelector('modal-dialog.modal-coming-soon');
    if (!modal) return;
    const content = modal.querySelector('.brn-modal__content');
    if (!content) return;
    const request = content.querySelector('[data-coming-soon-request]');
    const success = content.querySelector('[data-coming-soon-success]');
    if (success) success.hidden = false;
    if (request) request.hidden = true;
    const closeBtn = modal.querySelector('[data-modal-close]');
    if (closeBtn) setTimeout(() => closeBtn.focus(), 100);
    setTimeout(() => { if (typeof modal.hide === 'function') modal.hide(); else modal.removeAttribute('open'); }, 3000);
  }, true);
})();

// Stock validation on cart load/render
(function initCartStockValidation() {
  function getStockNoticeIconHtml() {
    const template = document.getElementById('CartStockNoticeIcon');
    return template ? template.innerHTML.trim() : '';
  }

  let previousStockLines = new Set();

  function validateCartStock() {
    const drawer = document.querySelector('#CartDrawer');
    if (!drawer) return;

    const inputs = drawer.querySelectorAll('input.js-counter-quantity[data-inventory-qty]');
    const iconHtml = getStockNoticeIconHtml();
    const problematicLines = [];

    inputs.forEach((input) => {
      const line = input.dataset.index;
      if (!line) return;
      const value = parseInt(input.value, 10);
      const stock = parseInt(input.dataset.inventoryQty, 10);
      if (!isNaN(stock) && value > stock) {
        problematicLines.push(line);
      }
    });

    // Clear notices for lines that are no longer problematic
    const activeSet = new Set(problematicLines);
    previousStockLines.forEach((line) => {
      if (!activeSet.has(line)) {
        document.dispatchEvent(new CustomEvent('cart-notices:capture', {
          detail: { line: `stock-line-${line}`, message: '' }
        }));
      }
    });
    // Clear legacy key if present
    document.dispatchEvent(new CustomEvent('cart-notices:capture', {
      detail: { line: 'stock-global', message: '' }
    }));
    previousStockLines = new Set(problematicLines);

    const msg = window.cartStrings?.soldOutNotice || 'Debes eliminar los productos sin stock.';
    const removeLabel = window.cartStrings?.stockRemove || 'Eliminar';

    problematicLines.forEach((line) => {
      const itemEl = drawer.querySelector(`[id$="-Item-${line}"]`);
      const productTitle = itemEl?.querySelector('.wt-cart__item__name')?.textContent.trim() || '';
      const message = `${msg} <button class="btn btn--text brn-stock-remove-all" data-stock-remove-lines="${line}" type="button">${removeLabel}</button>`;
      document.dispatchEvent(new CustomEvent('cart-notices:capture', {
        detail: { line: `stock-line-${line}`, message, productTitle, showClose: false, iconHtml }
      }));
    });

    updateCartBlockedState(problematicLines.length > 0);
  }

  function updateCartBlockedState(blocked) {
    const checkout = document.getElementById('CartDrawer-Checkout');
    const promos = document.querySelector('.wt-cart__drawer__promos');
    const stockSections = document.querySelectorAll('[data-cart-stock-section]');

    if (checkout) checkout.disabled = blocked;
    if (promos) promos.hidden = blocked;
    stockSections.forEach((el) => { el.hidden = blocked; });
  }

  document.addEventListener('click', (e) => {
    const btn = e.target.closest('.brn-stock-remove-all[data-stock-remove-lines]');
    if (!btn) return;
    const drawer = document.querySelector('#CartDrawer');
    if (!drawer) return;

    const lines = btn.dataset.stockRemoveLines.split(',').map(s => s.trim()).filter(Boolean);
    const updates = {};
    lines.forEach((line) => {
      const input = drawer.querySelector(`input.js-counter-quantity[data-index="${line}"]`);
      const variantId = input?.dataset.quantityVariantId;
      if (variantId) {
        intentionalRemoves.add(variantId);
        updates[variantId] = 0;
      }
    });

    if (!Object.keys(updates).length) return;
    btn.disabled = true;
    fetch(window.Shopify.routes.root + 'cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ updates })
    })
      .then(() => document.dispatchEvent(new CustomEvent('cart-drawer:refresh')))
      .catch(() => {
        btn.disabled = false;
        document.dispatchEvent(new CustomEvent('cart-drawer:refresh'));
      });
  });

  document.addEventListener('cart-drawer:refreshed', validateCartStock);
  subscribe(PUB_SUB_EVENTS.cartUpdate, validateCartStock);

  if (document.readyState === 'complete') {
    validateCartStock();
  } else {
    document.addEventListener('DOMContentLoaded', validateCartStock);
  }
})();

// Duplicate sizes notification
(function initCartDuplicatesValidation() {
  function validateCartDuplicates() {
    const drawer = document.querySelector('#CartDrawer');
    if (!drawer) return;

    drawer.querySelectorAll('[data-duplicate-notice]').forEach(el => el.remove());

    const items = new Map();
    drawer.querySelectorAll('li[data-product-id]').forEach((li) => {
      const id = li.dataset.productId;
      if (!id) return;
      if (!items.has(id)) items.set(id, []);
      items.get(id).push(li);
    });

    const msg = window.cartStrings?.duplicateSizesNotice || 'Hay varias tallas del mismo producto en tu cesta.';
    const iconHtml = document.getElementById('CartInfoNoticeIcon')?.innerHTML || '';
    items.forEach((els, id) => {
      if (els.length < 2) return;
      const notice = document.createElement('li');
      notice.dataset.duplicateNotice = id;
      notice.innerHTML = `<div class="brn-message wt-cart__error"><div class="brn-message__icon">${iconHtml}</div><div class="brn-message__text"><p class="wt-cart__error-text">${msg}</p></div></div>`;
      els[1].after(notice);
    });
  }

  document.addEventListener('cart-drawer:refreshed', validateCartDuplicates);
  subscribe(PUB_SUB_EVENTS.cartUpdate, validateCartDuplicates);

  if (document.readyState === 'complete') {
    validateCartDuplicates();
  } else {
    document.addEventListener('DOMContentLoaded', validateCartDuplicates);
  }
})();

// Auto-remove companion items (tote bag, gift wrap) when no regular items remain
(function initCompanionItemsCleanup() {
  function cleanupCompanionItems() {
    const drawer = document.querySelector('#CartDrawer');
    if (!drawer) return;

    const regularInputs = drawer.querySelectorAll('input.js-counter-quantity[data-quantity-variant-id]');
    if (regularInputs.length > 0) return;

    const updates = {};

    document.querySelectorAll('[data-cart-banner][data-line-key]').forEach(banner => {
      if (banner.dataset.variantId) {
        const id = String(banner.dataset.variantId);
        intentionalRemoves.add(id);
        updates[id] = 0;
      }
    });

    const giftSummary = document.querySelector('[data-gift-summary][data-variant-ids]');
    if (giftSummary?.dataset.variantIds) {
      giftSummary.dataset.variantIds.split(',').forEach(id => {
        const trimmed = id.trim();
        if (trimmed) { intentionalRemoves.add(trimmed); updates[trimmed] = 0; }
      });
    }

    if (!Object.keys(updates).length) return;

    // Hide the entire drawer body immediately to avoid any flash while async cleanup runs
    const body = drawer.querySelector('.wt-cart__drawer__body');
    if (body) body.hidden = true;

    const cartDrawerEl = document.querySelector('cart-drawer');
    const sectionIds = cartDrawerEl?.getSectionsToRender().map(s => s.id) || [];

    fetch(window.Shopify.routes.root + 'cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ updates })
    })
      .then(() => fetch(`${window.Shopify.routes.root}?sections=${sectionIds.join(',')}`))
      .then(r => r.json())
      .then(freshSections => {
        if (cartDrawerEl) cartDrawerEl.renderContents({ sections: freshSections }, false);
      });
  }

  document.addEventListener('cart-drawer:refreshed', cleanupCompanionItems);
  subscribe(PUB_SUB_EVENTS.cartUpdate, cleanupCompanionItems);
})();

// Cart loader overlay — handles item removal and wishlist-save states
(function initCartRemoveOverlay() {
  // How long the "removed · Undo" toast stays available. The drawer is only
  // blocked while the request is in flight; once the new cart is rendered the
  // toast lingers without blocking (toast-only mode) until this window ends.
  const TOAST_MS = 3000;

  let pending = null; // { undoData, token, postPromise, onUndo }
  let activeToast = null; // { text, type } while the toast should be shown
  let toastTimer = null;

  function getOverlay() {
    return document.querySelector('.wt-cart__loader');
  }

  function applyToast(overlay, { blocking }) {
    if (!overlay || !activeToast) return;
    // A freshly rendered overlay would fade in again; show it instantly instead.
    const instant = !blocking && !overlay.classList.contains('is-visible');
    if (instant) overlay.style.transition = 'none';
    setToastContent(overlay, activeToast);
    overlay.classList.toggle('wt-cart__loader--toast-only', !blocking);
    overlay.setAttribute('aria-hidden', 'false');
    overlay.classList.add('is-visible');
    if (instant) {
      void overlay.offsetWidth;
      overlay.style.transition = '';
    }
  }

  function scheduleHide(ms, token) {
    clearTimeout(toastTimer);
    if (ms <= 0) {
      hideOverlay();
      return;
    }
    toastTimer = setTimeout(() => {
      if (!token || pending?.token === token) hideOverlay();
    }, ms);
  }

  // renderContents() replaces #CartDrawer (and the loader inside it), so
  // re-apply the lingering toast to the freshly rendered overlay.
  document.addEventListener('cart-drawer:refreshed', () => {
    if (activeToast) applyToast(getOverlay(), { blocking: false });
  });

  function getItemData(line) {
    const input = document.getElementById(`Drawer-quantity-${line}`)
      || document.getElementById(`Quantity-${line}`);
    if (!input) return null;
    return {
      variantId: input.dataset.quantityVariantId,
      quantity: parseInt(input.value, 10) || 1
    };
  }

  function setToastContent(overlay, { text, type }) {
    const textEl = overlay.querySelector('.wt-cart__loader__toast__text');
    if (textEl) textEl.textContent = text;
    overlay.classList.toggle('wt-cart__loader--wishlist', type === 'wishlist');
  }

  function showError(message) {
    pending = null;
    activeToast = null;
    clearTimeout(toastTimer);
    const overlay = getOverlay();
    if (!overlay) return;
    const textEl = overlay.querySelector('.wt-cart__loader__toast__text');
    const undoBtn = overlay.querySelector('[data-cart-loader-undo]');
    if (textEl) textEl.textContent = message;
    if (undoBtn) undoBtn.hidden = true;
    overlay.classList.remove('wt-cart__loader--toast-only');
    setTimeout(() => {
      const o = getOverlay();
      if (o) {
        if (undoBtn) undoBtn.hidden = false;
        o.classList.remove('is-visible');
        o.setAttribute('aria-hidden', 'true');
      }
    }, 2000);
  }

  function showOverlay(line) {
    const overlay = getOverlay();
    if (!overlay) return;

    const cartDrawerEl = document.querySelector('cart-drawer');
    const undoData = getItemData(line);
    const token = {};

    const sections = cartDrawerEl?.getSectionsToRender().map(s => s.id) || [];

    // Capture companion IDs synchronously before fetch
    const companionIds = new Set();
    document.querySelectorAll('[data-cart-banner][data-line-key]').forEach(bannerEl => {
      if (bannerEl.dataset.variantId) companionIds.add(String(bannerEl.dataset.variantId));
    });
    const giftEl = document.querySelector('[data-gift-summary][data-variant-ids]');
    if (giftEl?.dataset.variantIds) {
      giftEl.dataset.variantIds.split(',').forEach(id => { const t = id.trim(); if (t) companionIds.add(t); });
    }

    // Removing the last regular item while companions (tote bag, gift wrap)
    // remain: drop everything in one cart/update.js round-trip instead of
    // change → update → sections GET.
    const regularInputs = document.querySelectorAll('#CartDrawer input.js-counter-quantity[data-quantity-variant-id]');
    const isLastRegular = companionIds.size > 0
      && regularInputs.length === 1
      && regularInputs[0].dataset.index === String(line)
      && undoData?.variantId;

    const parseCartResponse = (res) => res.text().then(state => {
      const parsed = JSON.parse(state);
      if (!res.ok || parsed.errors || parsed.status) {
        throw new Error(
          parsed.description || parsed.message ||
          (typeof parsed.errors === 'string' ? parsed.errors : null) ||
          window.cartStrings?.error || `HTTP ${res.status}`
        );
      }
      return parsed;
    });

    let postPromise;
    if (isLastRegular) {
      const updates = { [undoData.variantId]: 0 };
      companionIds.forEach(id => { intentionalRemoves.add(id); updates[id] = 0; });
      postPromise = fetch(window.Shopify.routes.root + 'cart/update.js', {
        ...fetchConfig(),
        body: JSON.stringify({ updates, sections, sections_url: window.location.pathname })
      }).then(parseCartResponse);
    } else {
      postPromise = fetch(`${routes.cart_change_url}`, {
        ...fetchConfig(),
        body: JSON.stringify({ line: Number(line), quantity: 0, sections, sections_url: window.location.pathname })
      })
        .then(parseCartResponse)
        .then(parsed => {
          if (!companionIds.size) return parsed;
          // Fallback (DOM prediction missed): only companions remain in the cart response
          const onlyCompanions = (parsed.items || []).every(item => companionIds.has(String(item.variant_id)));
          if (!onlyCompanions) return parsed;
          const updates = {};
          companionIds.forEach(id => { intentionalRemoves.add(id); updates[id] = 0; });
          return fetch(window.Shopify.routes.root + 'cart/update.js', {
            ...fetchConfig(),
            body: JSON.stringify({ updates, sections, sections_url: window.location.pathname })
          }).then(parseCartResponse);
        });
    }

    pending = { undoData, token, postPromise, onUndo: null };
    activeToast = { text: window.cartStrings?.itemRemoved || 'Product removed.', type: 'remove' };
    clearTimeout(toastTimer);
    const deadline = Date.now() + TOAST_MS;

    applyToast(overlay, { blocking: true });

    postPromise
      .then((removedState) => {
        if (pending && pending.token === token) {
          if (undoData?.variantId) intentionalRemoves.add(undoData.variantId);
          if (cartDrawerEl && removedState) {
            cartDrawerEl.renderContents(removedState, false);
          }
          applyToast(getOverlay(), { blocking: false });
          publish(PUB_SUB_EVENTS.cartUpdate, { source: 'cart-items' });
          scheduleHide(deadline - Date.now(), token);
        }
      })
      .catch((err) => {
        if (pending && pending.token === token) {
          console.error('[overlay] removal error:', err);
          showError(window.cartStrings?.error || err.message || 'Error');
        }
      });
  }

  function hideOverlay() {
    const overlay = getOverlay();
    if (!overlay) return;
    pending = null;
    activeToast = null;
    clearTimeout(toastTimer);
    overlay.classList.remove('is-visible', 'wt-cart__loader--wishlist', 'wt-cart__loader--toast-only');
    overlay.setAttribute('aria-hidden', 'true');
  }

  function undoRemoval() {
    if (!pending || !pending.undoData) return;
    const { variantId, quantity } = pending.undoData;
    const { postPromise } = pending;
    hideOverlay(); // sets pending = null, cancels render via token check

    const cartDrawerEl = document.querySelector('cart-drawer');
    const sections = cartDrawerEl?.getSectionsToRender().map(s => s.id) || [];

    const config = fetchConfig();
    config.body = JSON.stringify({ id: variantId, quantity, sections });

    // Wait for removal POST to settle before re-adding, to prevent race condition
    // where re-add lands before removal and Shopify processes them in wrong order
    Promise.resolve(postPromise)
      .catch(() => { })
      .then(() => fetch(`${routes.cart_add_url}`, config))
      .then(res => res.json())
      .then(response => {
        if (cartDrawerEl && typeof cartDrawerEl.renderContents === 'function') {
          cartDrawerEl.renderContents(response, false);
        } else {
          document.dispatchEvent(new CustomEvent('cart-drawer:refresh'));
        }
      })
      .catch((err) => {
        console.error('[overlay] undo error:', err);
        document.dispatchEvent(new CustomEvent('cart-drawer:refresh'));
      });
  }

  window.cartIntentionalRemove = (variantId) => intentionalRemoves.add(String(variantId));

  // Public API: show the overlay with custom content (e.g. wishlist save)
  window.showCartLoader = function ({ text, type = 'remove', onUndo = null }) {
    const overlay = getOverlay();
    if (!overlay) return;
    pending = { undoData: null, token: null, postPromise: null, onUndo };
    activeToast = { text, type };
    applyToast(overlay, { blocking: true });
    scheduleHide(TOAST_MS, null);
  };

  document.addEventListener('cart:remove-requested', ({ detail }) => {
    showOverlay(detail.line);
  });

  document.addEventListener('click', (e) => {
    if (!e.target.closest('[data-cart-loader-undo]')) return;
    if (pending?.onUndo) {
      const cb = pending.onUndo;
      hideOverlay();
      cb();
    } else {
      undoRemoval();
    }
  });
})();
